import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Coupon scheduling/pricing rules, owned by the admin panel
 * (הגדרות מערכת -> זמנים ומחירים, stored at `appSettings/coupons`).
 *
 * The defaults below must stay in sync with the admin panel's own fallbacks — they are what
 * both sides use before the settings document has ever been saved.
 */
export const DEFAULT_MIN_RANGE_DAYS = 3;
export const DEFAULT_PRICE_PER_DAY = 20;

export interface CouponSettings {
  /** Minimum publish duration, in whole days (inclusive of the first and last day). */
  minRangeDays: number;
  /**
   * Price per day in ILS, charged per selected community:
   * `pricePerDay × communities × days`. Consumer-app purchases only — coupons published
   * through the mefarsemim panel are priced by the deal agreed with the advertiser.
   */
  pricePerDay: number;
}

export const DEFAULT_COUPON_SETTINGS: CouponSettings = {
  minRangeDays: DEFAULT_MIN_RANGE_DAYS,
  pricePerDay: DEFAULT_PRICE_PER_DAY,
};

const positiveOr = (value: any, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const nonNegativeOr = (value: any, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

export const parseCouponSettings = (data: any): CouponSettings => ({
  minRangeDays: Math.floor(positiveOr(data?.minRangeDays, DEFAULT_MIN_RANGE_DAYS)),
  pricePerDay: nonNegativeOr(data?.pricePerDay, DEFAULT_PRICE_PER_DAY),
});

/**
 * Live subscription to the admin's scheduling settings. A missing document and a failed read
 * both fall back to the defaults, so the publish flow never blocks on this.
 */
export const useCouponSettings = (): CouponSettings => {
  const [settings, setSettings] = useState<CouponSettings>(DEFAULT_COUPON_SETTINGS);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'appSettings', 'coupons'),
      snap => setSettings(snap.exists() ? parseCouponSettings(snap.data()) : DEFAULT_COUPON_SETTINGS),
      () => setSettings(DEFAULT_COUPON_SETTINGS),
    );
    return () => unsub();
  }, []);

  return settings;
};

/** Local midnight of the given day — every coupon runs 00:00 to 00:00. */
export const toLocalMidnight = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** `endAt` is stored as an exclusive boundary: midnight of the day after the last day. */
export const lastDayToEndExclusive = (lastDay: Date): Date => {
  const d = toLocalMidnight(lastDay);
  d.setDate(d.getDate() + 1);
  return d;
};

/**
 * The earliest day a coupon may start: tomorrow.
 * A coupon runs from 00:00 of its start day, so "today" would mean a start time that has
 * already passed — there is no partial first day.
 */
export const earliestStartDate = (): Date => {
  const d = toLocalMidnight(new Date());
  d.setDate(d.getDate() + 1);
  return d;
};

/** Inclusive day count between two chosen days (same day = 1). */
export const daysInclusive = (start: Date, lastDay: Date): number => {
  const a = toLocalMidnight(start).getTime();
  const b = toLocalMidnight(lastDay).getTime();
  return Math.floor((b - a) / 86400000) + 1;
};
